import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Play, Search, Loader2, Info, ExternalLink, List } from 'lucide-react';
import { summarizationService } from '../services/summarizationService';
import SummarizationDashboard from '../components/SummarizationDashboard';
import QAFloatingButton from '../components/QAFloatingButton';

const Summarizations = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' or 'manual'
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [summaryResult, setSummaryResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Form states
  const [newSummarization, setNewSummarization] = useState({
    transcriptionJobId: '',
  });
  
  const [statusCheck, setStatusCheck] = useState({
    jobId: '',
  });

  const [summaryView, setSummaryView] = useState({
    jobId: '',
  });

  const [generatedSearch, setGeneratedSearch] = useState({
    companyName: '',
    quarter: 'Q1',
    date: '',
  });

  const [formErrors, setFormErrors] = useState({});

  // Clear error when form changes
  const handleFormChange = (formType, field, value) => {
    if (formType === 'new') {
      setNewSummarization(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'status') {
      setStatusCheck(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'view') {
      setSummaryView(prev => ({ ...prev, [field]: value }));
    } else if (formType === 'search') {
      setGeneratedSearch(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear errors for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStartSummarization = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate form
    const validation = summarizationService.validateSummarizationData(newSummarization);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setLoading(true);
    try {
      const result = await summarizationService.startSummarization(newSummarization);
      
      if (result.status === 'success' || result.job_id) {
        setSuccess(`Summarization started successfully! Job ID: ${result.job_id}`);
        setNewSummarization({ transcriptionJobId: '' });
        setFormErrors({});
      } else {
        setError(result.message || 'Failed to start summarization');
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    setError(null);
    setJobStatus(null);

    if (!statusCheck.jobId.trim()) {
      setFormErrors({ jobId: 'Summarization Job ID is required' });
      return;
    }
    
    setStatusLoading(true);
    try {
      const result = await summarizationService.getSummarizationStatus(statusCheck.jobId);
      setJobStatus(result);
      setFormErrors({});
    } catch (err) {
      setError(err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleViewSummary = async (e) => {
    e.preventDefault();
    setError(null);

    if (!summaryView.jobId.trim()) {
      setFormErrors({ summaryJobId: 'Job ID is required' });
      return;
    }

    // Navigate directly to the summary viewer page
    const jobId = String(summaryView.jobId.trim());
    console.log('View Summary - navigating to:', jobId);
    navigate(`/summary/${jobId}`);
  };

  const handleGeneratedSearch = async (e) => {
    e.preventDefault();
    setError(null);

    // Simple validation for search data
    if (!generatedSearch.companyName.trim()) {
      setFormErrors({ companyName: 'Company name is required' });
      return;
    }
    if (!generatedSearch.date) {
      setFormErrors({ date: 'Date is required' });
      return;
    }

    // Convert date format for backend (YYYY-MM-DD to YYYY_MM_DD)
    const formattedSearchData = {
      ...generatedSearch,
      date: generatedSearch.date ? generatedSearch.date.replace(/-/g, '_') : ''
    };

    setLoading(true);
    try {
      const result = await summarizationService.getGeneratedSummary(formattedSearchData);
      
      // If we have a job_id in the result, navigate to the summary viewer page
      if (result && result.job_id) {
        const jobId = String(result.job_id);
        console.log('Generated search - navigating to summary viewer with jobId:', jobId);
        navigate(`/summary/${jobId}`);
      } else {
        console.log('No job_id found, showing modal with result:', result);
        // Fallback to modal if no job_id available
        setSummaryResult(result);
        setShowModal(true);
      }
      setFormErrors({});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

    return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div className="container" style={{ paddingTop: '32px', paddingBottom: '32px' }}>
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">AI Summarization</h1>
            <p className="dashboard-subtitle">Generate AI-powered summaries from completed transcription jobs</p>
          </div>
          
          {/* View Mode Toggle */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
            <button
              onClick={() => setViewMode('dashboard')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'dashboard'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span>Dashboard View</span>
            </button>
            <button
              onClick={() => setViewMode('manual')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'manual'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
              <span>Summarize</span>
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="error-container">
            <div className="error-title">Error</div>
            <div className="error-message">
              {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
            </div>
        </div>
        )}

        {success && (
          <div className="success-container">
            <div className="success-message">{success}</div>
      </div>
        )}

        {/* Conditional Content Based on View Mode */}
        {viewMode === 'dashboard' ? (
          <SummarizationDashboard />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* Top Row: Start New Summarization and Check Job Status */}
          <div className="grid grid-cols-2" style={{ gap: '32px' }}>
            {/* Start New Summarization */}
            <div className="form-section">
              <h2 className="form-title">Start New Summarization</h2>
              <form onSubmit={handleStartSummarization}>
                <div style={{ marginBottom: '24px' }}>
                  <div className="form-group">
                    <label className="label">Transcription Job ID *</label>
                    <input
                      type="text"
                      value={newSummarization.transcriptionJobId}
                      onChange={(e) => handleFormChange('new', 'transcriptionJobId', e.target.value)}
                      placeholder="Enter completed transcription job ID"
                      className={`input ${formErrors.transcriptionJobId ? 'error' : ''}`}
                    />
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      Enter the job ID from a completed transcription job to generate a summary.
                    </div>
                    {formErrors.transcriptionJobId && (
                      <div className="error-text">{formErrors.transcriptionJobId}</div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  {loading ? (
                    <>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      <span>Starting...</span>
                    </>
                  ) : (
                    <>
                      <Play style={{ width: '16px', height: '16px' }} />
                      <span>Start Summarization</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Check Job Status */}
            <div className="form-section">
              <h2 className="form-title">Check Job Status</h2>
              <form onSubmit={handleCheckStatus}>
                <div style={{ marginBottom: '24px' }}>
                  <div className="form-group">
                    <label className="label">Summarization Job ID *</label>
                    <input
                      type="text"
                      value={statusCheck.jobId}
                      onChange={(e) => handleFormChange('status', 'jobId', e.target.value)}
                      placeholder="Enter summarization job ID"
                      className={`input ${formErrors.jobId ? 'error' : ''}`}
                    />
                    {formErrors.jobId && (
                      <div className="error-text">{formErrors.jobId}</div>
                    )}
        </div>
      </div>

                <button
                  type="submit"
                  disabled={statusLoading}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  {statusLoading ? (
                    <>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      <span>Checking...</span>
                    </>
                  ) : (
                    <>
                      <Search style={{ width: '16px', height: '16px' }} />
                      <span>Check Status</span>
                    </>
                  )}
                </button>
              </form>

              {/* Job Status Result */}
              {jobStatus && (
                <div style={{ 
                  marginTop: '24px', 
                  padding: '16px', 
                  background: '#f8fafc', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                    Job Status
                  </h4>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>Status:</span>
                      <span style={{ 
                        fontWeight: '500', 
                        color: jobStatus.status === 'completed' ? '#059669' : '#3b82f6' 
                      }}>
                        {jobStatus.status || 'Processing'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span>Progress:</span>
                      <span style={{ fontWeight: '500', color: '#1e293b' }}>
                        {(() => {
                          // Debug: log the full jobStatus to see what fields are available
                          console.log('Summarization Job Status Object:', jobStatus);
                          
                          // PRIORITY 1: Check for chunk information first (more informative than percentages)
                          if (jobStatus.completed_chunks && jobStatus.total_chunks) {
                            return `${jobStatus.completed_chunks}/${jobStatus.total_chunks} chunks`;
                          }
                          if (jobStatus.chunks_processed && jobStatus.total_chunks) {
                            return `${jobStatus.chunks_processed}/${jobStatus.total_chunks} chunks`;
                          }
                          if (jobStatus.processed_chunks && jobStatus.total_chunks) {
                            return `${jobStatus.processed_chunks}/${jobStatus.total_chunks} chunks`;
                          }
                          
                          // Check if chunks array exists and count completed ones
                          if (jobStatus.chunks && Array.isArray(jobStatus.chunks)) {
                            const completedChunks = jobStatus.chunks.filter(chunk => 
                              chunk.status === 'completed' || chunk.processed === true
                            ).length;
                            return `${completedChunks}/${jobStatus.chunks.length} chunks`;
                          }
                          
                          // Extract chunk info from message if available
                          if (jobStatus.message && typeof jobStatus.message === 'string') {
                            // Look for patterns like "Processing 8 chunks" or "8/12 chunks"
                            const chunkMatch = jobStatus.message.match(/(\d+)\s+processed\s+chunks/i) ||
                                             jobStatus.message.match(/processing\s+(\d+)\s+chunks/i);
                            if (chunkMatch) {
                              const processedCount = chunkMatch[1];
                              // Try to find total from other fields or estimate
                              const totalChunks = jobStatus.total_chunks || 
                                                 jobStatus.expected_chunks ||
                                                 (jobStatus.status === 'completed' ? processedCount : '?');
                              return `${processedCount}/${totalChunks} chunks`;
                            }
                            
                            // Look for direct chunk ratios in message
                            const ratioMatch = jobStatus.message.match(/(\d+)\/(\d+)\s*chunks?/i);
                            if (ratioMatch) {
                              return `${ratioMatch[1]}/${ratioMatch[2]} chunks`;
                            }
                          }
                          
                          // PRIORITY 2: Try direct progress fields (but chunks are preferred)
                          if (jobStatus.progress && typeof jobStatus.progress === 'string' && jobStatus.progress.includes('chunks')) {
                            return jobStatus.progress;
                          }
                          
                          // Check for any field that might indicate chunk progress
                          const allKeys = Object.keys(jobStatus);
                          const chunkKeys = allKeys.filter(key => 
                            key.toLowerCase().includes('chunk')
                          );
                          
                          if (chunkKeys.length > 0) {
                            console.log('Found potential chunk keys:', chunkKeys);
                            // Try to extract meaningful chunk info
                            for (const key of chunkKeys) {
                              const value = jobStatus[key];
                              if (typeof value === 'string' && value.includes('/')) {
                                return `${value} chunks`;
                              }
                              if (typeof value === 'number' && value > 0 && value < 1000) {
                                // Assume it's a chunk count
                                return `${value}/? chunks`;
                              }
                            }
                          }
                          
                          // PRIORITY 3: Fall back to percentages only if no chunk info available
                          if (jobStatus.progress_percentage) return `${jobStatus.progress_percentage}%`;
                          if (jobStatus.percentage) return `${jobStatus.percentage}%`;
                          if (jobStatus.progress && typeof jobStatus.progress === 'string') return jobStatus.progress;
                          
                          // Status-based fallbacks
                          if (jobStatus.status === 'processing' || jobStatus.status === 'running') {
                            return 'Processing...';
                          }
                          if (jobStatus.status === 'completed') {
                            return 'Complete';
                          }
                          
                          // Last resort - show all available fields for debugging
                          console.log('Available fields in jobStatus:', Object.keys(jobStatus));
                          return 'In Progress...';
                        })()}
                      </span>
                    </div>
                    {/* Progress Bar */}
                    {(() => {
                      let progressPercent = 0;
                      let showProgressBar = false;
                      
                      // PRIORITY 1: Calculate progress from chunk information
                      if (jobStatus.completed_chunks && jobStatus.total_chunks) {
                        progressPercent = Math.round((jobStatus.completed_chunks / jobStatus.total_chunks) * 100);
                        showProgressBar = true;
                      } else if (jobStatus.chunks_processed && jobStatus.total_chunks) {
                        progressPercent = Math.round((jobStatus.chunks_processed / jobStatus.total_chunks) * 100);
                        showProgressBar = true;
                      } else if (jobStatus.processed_chunks && jobStatus.total_chunks) {
                        progressPercent = Math.round((jobStatus.processed_chunks / jobStatus.total_chunks) * 100);
                        showProgressBar = true;
                      } else if (jobStatus.chunks && Array.isArray(jobStatus.chunks)) {
                        const completedChunks = jobStatus.chunks.filter(chunk => 
                          chunk.status === 'completed' || chunk.processed === true
                        ).length;
                        progressPercent = Math.round((completedChunks / jobStatus.chunks.length) * 100);
                        showProgressBar = true;
                      } else if (jobStatus.message && typeof jobStatus.message === 'string') {
                        // Try to extract chunk progress from message
                        const chunkMatch = jobStatus.message.match(/(\d+)\s+processed\s+chunks/i) ||
                                         jobStatus.message.match(/processing\s+(\d+)\s+chunks/i);
                        if (chunkMatch && jobStatus.total_chunks) {
                          progressPercent = Math.round((parseInt(chunkMatch[1]) / jobStatus.total_chunks) * 100);
                          showProgressBar = true;
                        } else if (chunkMatch && jobStatus.status === 'completed') {
                          // If completed, assume this is 100%
                          progressPercent = 100;
                          showProgressBar = true;
                        }
                      }
                      
                      // PRIORITY 2: Fall back to percentage fields only if no chunk info
                      if (!showProgressBar) {
                        if (jobStatus.progress_percentage) {
                          progressPercent = jobStatus.progress_percentage;
                          showProgressBar = true;
                        } else if (jobStatus.percentage) {
                          progressPercent = jobStatus.percentage;
                          showProgressBar = true;
                        } else if (jobStatus.status === 'completed') {
                          progressPercent = 100;
                          showProgressBar = true;
                        }
                      }
                      
                      if ((jobStatus.status === 'processing' || jobStatus.status === 'running') && !showProgressBar) {
                        // Show indeterminate progress for running jobs without specific progress
    return (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ 
                              width: '100%', 
                              height: '6px', 
                              background: '#e2e8f0', 
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: '30%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                                borderRadius: '3px',
                                animation: 'progress-slide 2s ease-in-out infinite'
                              }}></div>
                </div>
                            <style>
                              {`
                                @keyframes progress-slide {
                                  0% { transform: translateX(-100%); }
                                  50% { transform: translateX(250%); }
                                  100% { transform: translateX(-100%); }
                                }
                              `}
                            </style>
              </div>
                        );
                      }
                      
                      if (showProgressBar) {
                        return (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ 
                              width: '100%', 
                              height: '6px', 
                              background: '#e2e8f0', 
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${Math.min(progressPercent, 100)}%`,
                                height: '100%',
                                background: progressPercent === 100 ? '#10b981' : '#3b82f6',
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }}></div>
        </div>
      </div>
    );
                      }
                      
                      return null;
                    })()}
                    
                    {jobStatus.message && (
                      <div style={{ marginTop: '8px', fontSize: '13px', fontStyle: 'italic' }}>
                        {(() => {
                          // Make the message more contextual based on status
                          let message = jobStatus.message;
                          
                          // Remove confusing "more may be coming" for completed jobs
                          if (jobStatus.status === 'completed' && message.includes('more may be coming')) {
                            message = message.replace(/\s*\(more may be coming\)/gi, '');
                            message = message.replace(/,\s*more may be coming/gi, '');
                            // If it's completed, make it clear
                            if (message.includes('processed chunks')) {
                              message = message.replace('Returning', 'Processing completed with');
                            }
                          }
                          
                          // For processing jobs, keep the original message as it's informative
                          if ((jobStatus.status === 'processing' || jobStatus.status === 'running') && !message.includes('in progress') && !message.includes('processing')) {
                            // Add clarifying context for processing jobs
                            if (message.includes('processed chunks')) {
                              message = message + ' (processing in progress)';
                            }
                          }
                          
                          return message;
                        })()}
            </div>
          )}
        </div>
                </div>
              )}
            </div>
          </div>

          {/* View Summary Result */}
          <div className="form-section">
            <h2 className="form-title">View Summary Result</h2>
            <form onSubmit={handleViewSummary}>
              <div style={{ marginBottom: '24px' }}>
                <div className="form-group">
                <input
                  type="text"
                    value={summaryView.jobId}
                    onChange={(e) => handleFormChange('view', 'jobId', e.target.value)}
                    placeholder="Enter Job ID to view summary"
                    className={`input ${formErrors.summaryJobId ? 'error' : ''}`}
                  />
                  {formErrors.summaryJobId && (
                    <div className="error-text">{formErrors.summaryJobId}</div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                <ExternalLink style={{ width: '16px', height: '16px' }} />
                View Summary
              </button>
            </form>
          </div>

          {/* View Generated Summary */}
          <div className="form-section">
            <h2 className="form-title">View Generated Summary</h2>
            <form onSubmit={handleGeneratedSearch}>
              <div style={{ marginBottom: '24px' }}>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="label">Company Name *</label>
                <input
                  type="text"
                    value={generatedSearch.companyName}
                    onChange={(e) => handleFormChange('search', 'companyName', e.target.value)}
                    placeholder="Enter company name"
                    className={`input ${formErrors.companyName ? 'error' : ''}`}
                  />
                  {formErrors.companyName && (
                    <div className="error-text">{formErrors.companyName}</div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="label">Quarter *</label>
                  <select
                    value={generatedSearch.quarter}
                    onChange={(e) => handleFormChange('search', 'quarter', e.target.value)}
                    className="select"
                  >
                    <option value="Q1">Q1</option>
                    <option value="Q2">Q2</option>
                    <option value="Q3">Q3</option>
                    <option value="Q4">Q4</option>
                  </select>
                  {formErrors.quarter && (
                    <div className="error-text">{formErrors.quarter}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="label">Date *</label>
                  <input
                    type="date"
                    value={generatedSearch.date}
                    onChange={(e) => handleFormChange('search', 'date', e.target.value)}
                    className={`input ${formErrors.date ? 'error' : ''}`}
                  />
                  {formErrors.date && (
                    <div className="error-text">{formErrors.date}</div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                <BarChart3 style={{ width: '16px', height: '16px' }} />
                View Generated Summary
              </button>
            </form>
          </div>

        {/* Information Box */}
        <div className="info-box">
          <div className="info-title">
            <Info style={{ width: '16px', height: '16px' }} />
            AI Summarization Information
                  </div>
          <ul className="info-list">
            <li>Summarization requires a completed transcription job ID as input</li>
            <li>Jobs are processed asynchronously in the background</li>
            <li>Use the job status checker to monitor progress</li>
            <li>Completed summaries can be downloaded as text files</li>
          </ul>
                </div>
                
        {/* Modal for viewing summary */}
        {showModal && summaryResult && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                  Summary Result
                </h3>
                  <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  ×
                  </button>
              </div>
              <div style={{
                background: '#f8fafc',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                whiteSpace: 'pre-wrap',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                {summaryResult.summary || summaryResult.content || JSON.stringify(summaryResult, null, 2)}
              </div>
            </div>
          </div>
        )}
          </div>
        )}
      </div>
      
      {/* Q&A Floating Button */}
      <QAFloatingButton />
    </div>
  );
};

export default Summarizations; 